import type { Box, Contact } from './types';

/*
  Слой касания курьера (spec-3-motion §5): наш SVG и плёнка рядом с телом, в экранных координатах. Классы и
  outline чужого элемента не трогаются — всё рисуется здесь и уходит до подъёма.

  - Мениск: в момент касания из-под капли вдоль кромки растекается плёнка 2 px цвета ядра (60 %) с
    сужающимися концами; полудлина 0 → 0,7·размер тела за 240 мс. Лежит на линии рамки, внутрь не заходит:
    капля стоит на кромке, а не висит поверх.
  - Кольцо (только с разрешения подсвечивать чужое — «Кольцо на цели»): через 60 мс после касания от точки
    касания по контуру цели с отступом 3 px расходятся две струи, по часовой и против, и за 320 мс смыкаются
    напротив; на подъёме стекают обратно за 240 мс. Тон -fg — контраст не ниже 3:1.
  - Контур строки (тоже с разрешения): рамка вокруг строки с отступом 3 px проявляется за 200 мс, без струй —
    тело стоит не на строке, а на её блоке.
*/

export const MENISCUS_MS = 240;
export const RING_DELAY_MS = 60;
export const RING_FLOW_MS = 320;
export const RING_BACK_MS = 240;
export const CONTOUR_MS = 200;
/** Отступ кольца и контура от цели. */
const RING_GAP = 3;
/** Полудлина мениска — доля размера тела. */
const MENISCUS_REACH = 0.7;
/** Кривая оседания (spec-3-motion §5) — у плёнки та же. */
export const EASE_SETTLE = 'cubic-bezier(0.3, 0.7, 0.4, 1)';

const NS = 'http://www.w3.org/2000/svg';

interface Pt {
  x: number;
  y: number;
}

/** Контур цели с отступом: скруглённый прямоугольник по часовой, точки через ~2 px. */
function outline(b: Box, radii: [number, number, number, number]): Pt[] {
  const l = b.left - RING_GAP;
  const t = b.top - RING_GAP;
  const r = b.right + RING_GAP;
  const btm = b.bottom + RING_GAP;
  const [tl, tr, br, bl] = radii.map((v) => Math.min(v + RING_GAP, (r - l) / 2, (btm - t) / 2));
  const pts: Pt[] = [];
  const seg = (a: Pt, c: Pt) => {
    const n = Math.max(1, Math.ceil(Math.hypot(c.x - a.x, c.y - a.y) / 2));
    for (let i = 0; i < n; i++) pts.push({ x: a.x + ((c.x - a.x) * i) / n, y: a.y + ((c.y - a.y) * i) / n });
  };
  const arc = (cx: number, cy: number, rad: number, a0: number) => {
    const n = Math.max(2, Math.ceil((rad * Math.PI) / 2 / 2));
    for (let i = 0; i < n; i++) {
      const th = a0 + (Math.PI / 2) * (i / n);
      pts.push({ x: cx + rad * Math.cos(th), y: cy + rad * Math.sin(th) });
    }
  };
  seg({ x: l + tl, y: t }, { x: r - tr, y: t });
  arc(r - tr, t + tr, tr, -Math.PI / 2);
  seg({ x: r, y: t + tr }, { x: r, y: btm - br });
  arc(r - br, btm - br, br, 0);
  seg({ x: r - br, y: btm }, { x: l + bl, y: btm });
  arc(l + bl, btm - bl, bl, Math.PI / 2);
  seg({ x: l, y: btm - bl }, { x: l, y: t + tl });
  arc(l + tl, t + tl, tl, Math.PI);
  return pts;
}

const radiiOf = (el: Element): [number, number, number, number] => {
  const cs = getComputedStyle(el);
  return [
    parseFloat(cs.borderTopLeftRadius) || 0,
    parseFloat(cs.borderTopRightRadius) || 0,
    parseFloat(cs.borderBottomRightRadius) || 0,
    parseFloat(cs.borderBottomLeftRadius) || 0,
  ];
};

const pathOf = (pts: Pt[]) =>
  pts.map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ') + ' Z';

function stroke(svg: SVGSVGElement, d: string, cls: string): SVGPathElement {
  const p = document.createElementNS(NS, 'path');
  p.setAttribute('d', d);
  p.setAttribute('pathLength', '1');
  p.setAttribute('fill', 'none');
  p.setAttribute('stroke-width', '2');
  p.setAttribute('stroke-linecap', 'round');
  p.setAttribute('class', cls);
  svg.appendChild(p);
  return p;
}

const finished = (a: Animation) =>
  a.finished.then(
    () => undefined,
    () => undefined,
  );

export interface ContactLayer {
  clear(): void;
  /** Мениск по кромке; out — втянуть под каплю. */
  meniscus(c: Contact, k: number): { out(k: number): Promise<void> };
  /** Кольцо-обтекание цели от точки касания. */
  ring(
    target: Element,
    c: Contact,
    tone: 'brand' | 'success',
    k: number,
  ): { back(k: number): Promise<void>; off(): void };
  /** Контур строки. */
  contour(row: Element, tone: 'brand' | 'success', k: number): { off(k: number): Promise<void> };
}

export function makeContact(svg: SVGSVGElement, film: HTMLElement): ContactLayer {
  const clear = () => {
    svg.replaceChildren();
    film.replaceChildren();
  };

  const meniscus: ContactLayer['meniscus'] = (c, k) => {
    const half = MENISCUS_REACH * c.size;
    const el = document.createElement('div');
    el.className = 'courier-film absolute';
    el.style.left = `${c.x - half}px`;
    el.style.top = `${c.y - 1}px`;
    el.style.width = `${2 * half}px`;
    el.style.rotate = `${(Math.atan2(c.normal[1], c.normal[0]) * 180) / Math.PI + 90}deg`;
    film.appendChild(el);
    el.animate([{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }], {
      duration: MENISCUS_MS * k,
      easing: EASE_SETTLE,
      fill: 'forwards',
    });
    return {
      out: (kk) =>
        finished(
          el.animate([{ transform: 'scaleX(1)' }, { transform: 'scaleX(0)' }], {
            duration: 160 * kk,
            easing: 'ease-in',
            fill: 'forwards',
          }),
        ).then(() => el.remove()),
    };
  };

  const ring: ContactLayer['ring'] = (target, c, tone, k) => {
    const all = outline(target.getBoundingClientRect(), radiiOf(target));
    // Начало — точка контура напротив касания; струи — по часовой и против до противоположной стороны.
    let start = 0;
    let best = Infinity;
    all.forEach((p, i) => {
      const d = Math.hypot(p.x - c.x, p.y - c.y);
      if (d < best) {
        best = d;
        start = i;
      }
    });
    const cw = [...all.slice(start), ...all.slice(0, start)];
    const d = pathOf(cw);
    const cls = tone === 'success' ? 'stroke-success-fg' : 'stroke-brand-fg';
    const a = stroke(svg, d, cls);
    const b = stroke(svg, d, cls);
    const from = [
      { strokeDasharray: '0 1', strokeDashoffset: '0' },
      { strokeDasharray: '0 1', strokeDashoffset: '-1' },
    ];
    const to = [
      { strokeDasharray: '0.5 1', strokeDashoffset: '0' },
      { strokeDasharray: '0.5 1', strokeDashoffset: '-0.5' },
    ];
    const flow = (el: SVGPathElement, frames: [Keyframe, Keyframe], ms: number, delay = 0) =>
      el.animate(frames, { duration: ms, delay, easing: EASE_SETTLE, fill: 'both' });
    const anims = [
      flow(a, [from[0], to[0]], RING_FLOW_MS * k, RING_DELAY_MS * k),
      flow(b, [from[1], to[1]], RING_FLOW_MS * k, RING_DELAY_MS * k),
    ];
    let gone = false;
    return {
      back: (kk) => {
        if (gone) return Promise.resolve();
        anims.forEach((x) => x.cancel());
        const back = [
          flow(a, [to[0], from[0]], RING_BACK_MS * kk),
          flow(b, [to[1], from[1]], RING_BACK_MS * kk),
        ];
        return Promise.all(back.map(finished)).then(() => {
          a.remove();
          b.remove();
        });
      },
      off: () => {
        gone = true;
        anims.forEach((x) => x.cancel());
        a.remove();
        b.remove();
      },
    };
  };

  const contour: ContactLayer['contour'] = (row, tone, k) => {
    const r = row.getBoundingClientRect();
    const rect = document.createElementNS(NS, 'rect');
    const rad = (radiiOf(row)[0] || 0) + RING_GAP;
    rect.setAttribute('x', `${r.left - RING_GAP}`);
    rect.setAttribute('y', `${r.top - RING_GAP}`);
    rect.setAttribute('width', `${r.width + 2 * RING_GAP}`);
    rect.setAttribute('height', `${r.height + 2 * RING_GAP}`);
    rect.setAttribute('rx', `${rad}`);
    rect.setAttribute('fill', 'none');
    rect.setAttribute('stroke-width', '2');
    rect.setAttribute('class', tone === 'success' ? 'stroke-success-fg' : 'stroke-brand-fg');
    svg.appendChild(rect);
    rect.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: CONTOUR_MS * k,
      easing: 'ease-out',
      fill: 'both',
    });
    return {
      off: (kk) =>
        finished(
          rect.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 160 * kk, fill: 'forwards' }),
        ).then(() => rect.remove()),
    };
  };

  return { clear, meniscus, ring, contour };
}
